import React, { useState } from 'react';
import { MapContainer, TileLayer, useMap, Marker, Popup } from 'react-leaflet';
import { LatLngBounds, Icon } from 'leaflet';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSnackbar } from 'notistack';
import { calculateMapBounds, formatCoordinates, getGoogleMapsUrl } from '../../utils/mapUtils';
import 'leaflet/dist/leaflet.css';
import styles from './FarmLocationMap.module.scss';

// Fix for default marker icon in Leaflet with React
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface FarmLocationMapProps {
  latitude: number;
  longitude: number;
  area?: number | null;
}

// Component to fit map bounds
const FitBounds: React.FC<{ bounds: LatLngBounds }> = ({ bounds }) => {
  const map = useMap();
  React.useEffect(() => {
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [map, bounds]);
  return null;
};

export const FarmLocationMap: React.FC<FarmLocationMapProps> = ({
  latitude,
  longitude,
  area,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [mapKey, setMapKey] = useState(0);

  const bounds = calculateMapBounds(latitude, longitude, area);
  const coordinatesText = formatCoordinates(latitude, longitude);
  const googleMapsUrl = getGoogleMapsUrl(latitude, longitude);

  const handleCopyCoordinates = async () => {
    try {
      await navigator.clipboard.writeText(coordinatesText);
      enqueueSnackbar('Coordenadas copiadas', { variant: 'success' });
    } catch (error) {
      console.error('Failed to copy coordinates:', error);
      enqueueSnackbar('Error al copiar coordenadas', { variant: 'error' });
    }
  };

  const handleOpenInGoogleMaps = () => {
    window.open(googleMapsUrl, '_blank');
  };

  // Force re-render to ensure map initializes properly
  React.useEffect(() => {
    setMapKey(prev => prev + 1);
  }, [latitude, longitude, area]);

  return (
    <Box className={styles.mapContainer}>
      <Box className={styles.mapWrapper}>
        <MapContainer
          key={mapKey}
          center={[latitude, longitude]}
          zoom={13}
          className={styles.map}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.esri.com/">Esri</a> &copy; <a href="https://www.google.com/maps">Google</a>'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
          <Marker position={[latitude, longitude]}>
            <Popup>
              {coordinatesText}
            </Popup>
          </Marker>
          <FitBounds bounds={bounds} />
        </MapContainer>
        <Box className={styles.mapOverlay}>
          <Tooltip title="Abrir en Google Maps">
            <IconButton
              className={styles.openButton}
              onClick={handleOpenInGoogleMaps}
              size="small"
              sx={{
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box className={styles.mapActions}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopyCoordinates}
          fullWidth
          sx={{
            textTransform: 'none',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          Copiar coordenadas
        </Button>
      </Box>
    </Box>
  );
};

